// Pure Connection Relationships module.
//
// Answers "who else does this person connect me to?" from data the app already
// holds: the owner's own entries (their Origins form a tree) and the Discovery
// Graph (public topology). No React, no Firebase.

import {
  FACET_CONTEXTS,
  personKeyOf,
  resolveOriginTarget,
} from "./rolodexContext";

function matchesTarget(target, entryId, personKey) {
  if (!target) return false;
  if (entryId && target.entryId === entryId) return true;
  return Boolean(personKey && target.personKey && target.personKey === personKey);
}

/**
 * Entries placed on the map *through* this person — the people you met via
 * them. Searches every context when `context` is omitted, so the card shows a
 * complete picture regardless of which facet is on screen.
 */
export function connectionsThrough(entries = [], entry, context = null) {
  if (!entry?.id) return [];
  const entryId = entry.id;
  const personKey = personKeyOf(entry);
  const contexts = context ? [context] : FACET_CONTEXTS;

  return entries.filter((candidate) => {
    if (!candidate || candidate.id === entryId) return false;
    return contexts.some((facetContext) =>
      matchesTarget(
        resolveOriginTarget(candidate.facets?.[facetContext]?.origin, entries),
        entryId,
        personKey
      )
    );
  });
}

/**
 * People in the Discovery Graph reachable *through* this person — the
 * introductions they could make. Only registered people appear here, because
 * name-only contacts publish no Discovery Edge.
 */
export function introductionsThrough(graph, entry) {
  const personUid = entry?.recipient_uid;
  if (!personUid || !graph?.nodes?.length) return [];
  return graph.nodes.filter(
    (node) => node.parentUid === personUid && node.id !== personUid
  );
}

/** Everything the Relationships card renders, in one call. */
export function relationshipSummary({ entries = [], entry, graph = null, context = null }) {
  const through = connectionsThrough(entries, entry, context);
  const introductions = introductionsThrough(graph, entry);
  return {
    through,
    introductions,
    total: through.length + introductions.length,
  };
}
